import React, { useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import ViewsNav from '../components/ViewsNav';

const PLACEHOLDERS = [
  '[GUEST_NAME]',
  '[CHECKIN_TIME]',
  '[CHECKOUT_TIME]',
  '[DOOR_CODE]',
  '[WIFI_PASSWORD]',
  '[WIFI_NETWORK]',
  '[NUM_NIGHTS]',
];

const TEMPLATES = {
  "It's Happy Hour": {
    'pre-arrival': {
      label: 'Pre-Arrival',
      subject: 'Getting ready for your stay at It\'s Happy Hour!',
      body: `Hi [GUEST_NAME],

We're so excited to welcome you to It's Happy Hour in just a few days! Here are the details you'll need for a smooth arrival:

CHECK-IN: [CHECKIN_TIME]
DOOR CODE: [DOOR_CODE]
PARKING: Pull into the driveway — fits 2 cars comfortably. Street parking also available on Forest St.
WIFI NETWORK: [WIFI_NETWORK]
WIFI PASSWORD: [WIFI_PASSWORD]

A few quick notes:
- The pool is heated and ready for you
- Beach chairs and umbrellas are in the garage
- Seaside shops and restaurants are a short walk away
- The grill on the back deck is propane — tank should be full

If you have any questions before arrival, don't hesitate to reach out. We hope you have an amazing stay!

Warm regards,
Your Hosts`,
    },
    'checkin-day': {
      label: 'Check-in Day',
      subject: 'Today\'s the day — welcome to It\'s Happy Hour!',
      body: `Good morning [GUEST_NAME]!

Today's the day! Just a quick reminder:

CHECK-IN: [CHECKIN_TIME]
DOOR CODE: [DOOR_CODE]
ADDRESS: 600 Forest St, Santa Rosa Beach, FL 32459

When you arrive:
1. Enter through the front door using the code above
2. AC is pre-set to 72°F — thermostat is in the hallway
3. Welcome binder on the kitchen counter has all the house details
4. Pool towels are in the linen closet by the back door

Have a wonderful stay! We're just a text away if you need anything.`,
    },
    'checkout-reminder': {
      label: 'Check-out Reminder',
      subject: 'Check-out reminder — It\'s Happy Hour',
      body: `Hi [GUEST_NAME],

We hope you've had an incredible stay at It's Happy Hour! Just a friendly reminder that check-out is tomorrow.

CHECKOUT TIME: [CHECKOUT_TIME]

Before you head out:
- Start the dishwasher if there are dishes
- Place used towels in the bathtubs
- Take all trash to the outdoor bins (side of house)
- Lock the front door — it auto-locks, just make sure it clicks
- Leave the AC set to 78°F
- No need to strip the beds — our cleaning team will handle that

Please leave the key code as-is. Safe travels home, and thank you for staying with us!`,
    },
    'review-request': {
      label: 'Review Request',
      subject: 'How was your stay at It\'s Happy Hour?',
      body: `Hi [GUEST_NAME],

We hope you made it home safely and are still riding the vacation vibes! We truly enjoyed hosting you at It's Happy Hour.

If you have a moment, we'd really appreciate a review of your stay. Your feedback helps future guests discover our little slice of Seaside, and it means the world to us as hosts.

Thank you again for choosing It's Happy Hour — we'd love to welcome you back anytime!

Warm regards,
Your Hosts`,
    },
    'mid-stay': {
      label: 'Mid-Stay Check-in',
      subject: 'How\'s everything going at It\'s Happy Hour?',
      body: `Hi [GUEST_NAME],

Just checking in to make sure everything is going great at It's Happy Hour! You're about halfway through your [NUM_NIGHTS]-night stay and we want to make sure you have everything you need.

Is the pool temperature good? Need any restaurant recommendations? Anything at all — just let us know.

Enjoy the rest of your trip!`,
    },
  },
  '4400 Windswept': {
    'pre-arrival': {
      label: 'Pre-Arrival',
      subject: 'Getting ready for your stay at 4400 Windswept!',
      body: `Hi [GUEST_NAME],

We're looking forward to welcoming you to 4400 Windswept at Kiawah Island! Here are the details for your arrival:

CHECK-IN: [CHECKIN_TIME]
DOOR CODE: [DOOR_CODE]
PARKING: Designated spot at the unit — see the welcome guide for the exact location.
WIFI NETWORK: [WIFI_NETWORK]
WIFI PASSWORD: [WIFI_PASSWORD]

A few things to know:
- Kiawah Island gate access — you'll receive a gate pass from the resort
- Bikes are available for exploring the island
- The beach is a short walk from the unit
- East Beach Village has dining and shops within walking distance

Please reach out if you have any questions before you arrive. We can't wait for you to experience Kiawah!

Best,
Your Hosts`,
    },
    'checkin-day': {
      label: 'Check-in Day',
      subject: 'Welcome to 4400 Windswept — check-in today!',
      body: `Good morning [GUEST_NAME]!

Today's the day! Quick check-in details:

CHECK-IN: [CHECKIN_TIME]
DOOR CODE: [DOOR_CODE]
ADDRESS: 4400 Sea Forest Dr, Johns Island, SC 29455

When you arrive:
1. Check in at the Kiawah Island gate — have your confirmation handy
2. Enter the unit using the door code above
3. Welcome binder on the kitchen island has house details and local tips
4. Beach towels and chairs are in the front closet

Enjoy Kiawah! Text us anytime if you need a thing.`,
    },
    'checkout-reminder': {
      label: 'Check-out Reminder',
      subject: 'Check-out reminder — 4400 Windswept',
      body: `Hi [GUEST_NAME],

Hope you've had a wonderful time on Kiawah Island! Just a reminder that check-out is tomorrow.

CHECKOUT TIME: [CHECKOUT_TIME]

Before you leave:
- Run the dishwasher if there are dirty dishes
- Place used towels in the bathtubs
- Take trash to the designated bins (see welcome binder for location)
- Lock up and make sure the door clicks shut
- Return any borrowed resort equipment (bikes, beach gear)
- Leave the thermostat set to 76°F

Thank you for being such great guests — safe travels!`,
    },
    'review-request': {
      label: 'Review Request',
      subject: 'How was your Kiawah Island stay?',
      body: `Hi [GUEST_NAME],

We hope you're settling back in at home with some wonderful Kiawah memories! We loved hosting you at 4400 Windswept.

If you have a quick moment, a review of your stay would mean so much to us. It helps other guests find our place and lets us know how we're doing.

Thank you for choosing 4400 Windswept — the door is always open for a return visit!

Best,
Your Hosts`,
    },
    'mid-stay': {
      label: 'Mid-Stay Check-in',
      subject: 'How\'s everything at 4400 Windswept?',
      body: `Hi [GUEST_NAME],

Just a quick check-in — you're about halfway through your [NUM_NIGHTS]-night stay at 4400 Windswept! We hope you're loving Kiawah Island.

Need any recommendations for dining, activities, or beach spots? Is everything in the unit working well?

Don't hesitate to reach out. Enjoy the rest of your stay!`,
    },
  },
};

const TEMPLATE_ORDER = ['pre-arrival', 'checkin-day', 'checkout-reminder', 'review-request', 'mid-stay'];
const PROPERTIES = ["It's Happy Hour", '4400 Windswept'];

function highlightPlaceholders(text) {
  const parts = text.split(/(\[[A-Z_]+\])/g);
  return parts.map((part, i) =>
    PLACEHOLDERS.includes(part)
      ? <span key={i} className="placeholder">{part}</span>
      : part
  );
}

export default function Templates({ session, currentView, onNavigate }) {
  const [selectedProperty, setSelectedProperty] = useState(PROPERTIES[0]);
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_ORDER[0]);
  const [editedBodies, setEditedBodies] = useState({});
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  const templateData = TEMPLATES[selectedProperty]?.[selectedTemplate];
  const editKey = `${selectedProperty}::${selectedTemplate}`;
  const currentBody = editedBodies[editKey] ?? templateData?.body ?? '';

  const handleBodyChange = useCallback((e) => {
    setEditedBodies(prev => ({ ...prev, [editKey]: e.target.value }));
  }, [editKey]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentBody);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = currentBody;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [currentBody]);

  const handleReset = useCallback(() => {
    setEditedBodies(prev => {
      const next = { ...prev };
      delete next[editKey];
      return next;
    });
    setEditing(false);
  }, [editKey]);

  const isModified = editedBodies[editKey] !== undefined;

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>

        <ViewsNav currentView={currentView} onNavigate={onNavigate} />

        <div className="sidebar-section">
          <div className="sidebar-section-label">Property</div>
          {PROPERTIES.map(p => (
            <div
              key={p}
              className={`sidebar-item ${selectedProperty === p ? 'active' : ''}`}
              onClick={() => setSelectedProperty(p)}
            >
              {p}
            </div>
          ))}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-label">Templates</div>
          {TEMPLATE_ORDER.map(key => (
            <div
              key={key}
              className={`sidebar-item ${selectedTemplate === key ? 'active' : ''}`}
              onClick={() => { setSelectedTemplate(key); setEditing(false); }}
            >
              {TEMPLATES[selectedProperty][key].label}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{TEMPLATE_ORDER.length}</span>
            <span className="stat-label">Templates</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{selectedProperty === "It's Happy Hour" ? 'HH' : 'WS'}</span>
            <span className="stat-label">{selectedProperty}</span>
          </div>
          <button
            className={`sync-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
          >
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>

        {/* Content */}
        <div className="content-area">
          <div className="content-header">
            <h2>{templateData?.label} — {selectedProperty}</h2>
            <p>Subject: {templateData?.subject}</p>
          </div>

          <div className="template-card">
            <div className="template-toolbar">
              <button
                className={`btn btn-secondary ${editing ? 'active-edit' : ''}`}
                onClick={() => setEditing(!editing)}
              >
                {editing ? 'Preview' : 'Edit'}
              </button>
              {isModified && (
                <button className="btn btn-secondary" onClick={handleReset}>
                  Reset to Default
                </button>
              )}
              <button className="btn btn-primary" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {editing ? (
              <textarea
                className="template-editor"
                value={currentBody}
                onChange={handleBodyChange}
              />
            ) : (
              <div className="template-preview">
                {currentBody.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {highlightPlaceholders(line)}
                    {i < currentBody.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <div className="placeholder-legend">
            <div className="placeholder-legend-title">Placeholders — replace before sending:</div>
            <div className="placeholder-chips">
              {PLACEHOLDERS.map(p => (
                <span key={p} className="placeholder-chip">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
